import { FC, useRef } from 'react'
import styles from '../TicketLanding.module.css'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { ArrowRightIcon } from '@/atoms/Icons/ArrowRightIcon'
import { Image } from '@/atoms/Image'
import { Text } from '@/atoms/Text'
import { EmblaCarousel, EmblaSlide } from '@/molecules/EmblaCarousel'
import { useTranslate } from '@/utils/translate/translate-client'
import { GradientWrapper } from '@/views/TicketsLandingView/NewTicketsLandingView/components/GraidentWrapper'
import { RadialGradient } from '@/views/TicketsLandingView/NewTicketsLandingView/components/RadialGradient'
import { PastTheatricalRelease } from '@/views/TicketsLandingView/NewTicketsLandingView/types'

interface Props {
  releases: PastTheatricalRelease[]
  className?: string
}

export const PastReleases: FC<Props> = ({ releases, className }) => {
  const containerRoot = useRef<HTMLDivElement>(null)
  const { t } = useTranslate('tickets')

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const mobileItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  }

  const desktopItemVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  }

  return (
    <section ref={containerRoot} className="relative overflow-hidden">
      <div className={classNames('flex flex-col gap-4 md:gap-10', className)}>
        <motion.div
          className={styles.layoutContainer}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Text className="photon-title-md md:photon-heading-lg xl:photon-heading-xl" weight="bold" color="white">
            {t('pastReleases', 'Past Releases')}
          </Text>
        </motion.div>

        {/*mobile*/}
        <motion.div
          className={classNames('grid grid-cols-2 gap-3 md:hidden', styles.layoutContainer)}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {releases.map((release) => {
            return (
              <motion.div key={release.theatricalSlug} variants={mobileItemVariants}>
                <PastRelease image={release.image} link={release.link} />
              </motion.div>
            )
          })}
        </motion.div>

        {/*/!*desktop*!/*/}
        <motion.div
          className="hidden w-full md:block md:w-full"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          <EmblaCarousel
            includeArrows
            options={{ dragFree: true }}
            containerClassName={classNames('gap-6 w-full', styles.layoutContainer)}
            className="w-full"
          >
            {releases.map((release) => {
              return (
                <EmblaSlide
                  fadeOutOfView
                  containerRootOverride={containerRoot.current}
                  className="w-full max-w-[182px] xl:max-w-[250px]"
                  key={release.theatricalSlug}
                >
                  <motion.div variants={desktopItemVariants}>
                    <PastRelease image={release.image} link={release.link} />
                  </motion.div>
                </EmblaSlide>
              )
            })}
          </EmblaCarousel>
        </motion.div>
      </div>

      <RadialGradient
        className="absolute left-0 top-1/2 -z-10 -translate-x-1/2 -translate-y-1/2 opacity-10"
        from="50% 50% at 50% 50%, #21CEA0 0%"
        to="rgba(33, 206, 160, 0) 100%"
        size="200%"
      />
    </section>
  )
}

const PastRelease = ({ image, link }: { image: string; link: string }) => {
  return (
    <GradientWrapper className="group rounded-xl hover:shadow-dark-2">
      <Link
        href={link}
        className="aspect-h-3 aspect-w-2 relative block overflow-hidden rounded-xl border border-transparent hover:border-core-gray-200"
      >
        <Image
          sizes="(min-width: 768px) 250px, 50vw"
          src={image}
          fill
          alt="poster"
          className="object-cover transition-transform duration-[500ms] ease-in-out group-hover:scale-[1.02]"
        />

        <div className="absolute inset-0 grid items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100">
          <div className="rounded-full bg-white/20 p-3 backdrop-blur-[48px]">
            <ArrowRightIcon size={24} color="white" />
          </div>
        </div>
      </Link>
    </GradientWrapper>
  )
}
